import { X } from "lucide-react";
import { useId, useRef, type ReactNode } from "react";
import { useDialogoMecanica } from "../dialogoMecanica";
import { cn } from "../../lib/utils";

/**
 * GAVETA LATERAL canônica — o `Modal` deitado na direita.
 *
 * 🔴 Por que ela existe (auditoria de CX, 29/07, item B1): as três gavetas do
 * financeiro — A Receber, A Pagar e o dossiê do sócio — são a superfície de
 * interação PRINCIPAL dessas telas, e eram `fixed inset-0` artesanais: sem foco
 * preso, sem `role="dialog"`, sem `aria-modal`, sem Esc, sem travar a rolagem do
 * corpo, sem restaurar o foco de quem abriu, e com um X de alvo de toque de
 * ~28px. O `Modal.tsx` entrega tudo isso desde sempre; as gavetas passavam ao
 * lado dele porque a ANATOMIA é outra (nasce colada na direita, ocupa a altura
 * inteira, tem cabeçalho próprio).
 *
 * Então a anatomia é daqui e a MECÂNICA é a do Modal, literalmente a mesma
 * função (`useDialogoMecanica`) — não uma cópia que diverge no primeiro conserto.
 *
 * ⚠️ Só CASCA. O conteúdo da gaveta é de quem a usa: esta camada não sabe (nem
 * pode saber) o que é baixa com cartão, estorno, anexo ou régua de cobrança.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  larguraClassName = "max-w-md",
  painelClassName = "overflow-y-auto border-l border-border bg-card shadow-xl",
  backdropClassName = "bg-black/30",
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Nome acessível da gaveta (vira `aria-labelledby`). O leitor de tela anuncia
   * isto ao entrar — "Condomínio Vila Nova", "Dossiê do sócio".
   */
  title: string;
  children: ReactNode;
  /** Largura do painel (`max-w-md`, `max-w-[560px]`…). */
  larguraClassName?: string;
  /** Classes do PAINEL além do posicionamento (rolagem, borda, fundo, sombra). */
  painelClassName?: string;
  /** Classes do véu atrás do painel. */
  backdropClassName?: string;
  /**
   * Clique-fora e Esc fecham. Default TRUE aqui — ao contrário do `Modal`, cujo
   * default é FALSE por decisão do Ruddy (02/06) pra não perder formulário
   * digitado por engano. A gaveta é superfície de LEITURA e ação (o formulário
   * que ela abre é um Modal por cima), e as três de hoje já fechavam no clique
   * no véu: manter isso é o que garante "mudou a casca, não mudou o uso". Quem
   * puser campo de digitar direto numa gaveta passa `dismissible={false}`.
   */
  dismissible?: boolean;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogoMecanica({ open, onClose, dismissible, dialogRef });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={cn("absolute inset-0", backdropClassName)} onClick={dismissible ? onClose : undefined} />
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn("relative flex h-full w-full flex-col", larguraClassName, painelClassName)}
      >
        {/* O nome acessível sem mexer no desenho: os cabeçalhos das três gavetas
            já mostram o nome do título/sócio com o tamanho e o peso certos —
            duplicar visualmente seria mudança de conteúdo. */}
        <span id={titleId} className="sr-only">{title}</span>
        {children}
      </div>
    </div>
  );
}

/**
 * O X da gaveta — alvo de toque de 40px e `aria-label`, iguais aos do `Modal`.
 *
 * Fica à parte porque cada gaveta monta o próprio cabeçalho (o de A Receber tem
 * chip de situação + valor grande; o do sócio tem avatar). O botão é que não
 * pode continuar sendo escrito de novo, cada vez de um jeito — dois dos três
 * estavam sem `aria-label` (CX, item C6).
 */
export function FecharGaveta({ onClose, className }: { onClose: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Fechar"
      className={cn(
        "flex min-h-[40px] min-w-[40px] flex-shrink-0 items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
        className,
      )}
    >
      <X className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
